/**
 * Table Editor Module
 * Context menu, row/col ops, merge/split, borders, cell styling,
 * and grid picker for table insertion.
 */

let editorEl = null;
let pageRef = null;
let activeTable = null;
let toolbar = null;
let contextMenu = null;

// --- Floating Table Toolbar ---

function createToolbar() {
    if (toolbar) toolbar.remove();
    toolbar = document.createElement('div');
    toolbar.className = 'table-edit-toolbar';
    toolbar.contentEditable = 'false';
    toolbar.innerHTML = `
        <button data-table-action="addRowAbove" title="Insert Row Above">&#8593; Row</button>
        <button data-table-action="addRowBelow" title="Insert Row Below">&#8595; Row</button>
        <button data-table-action="addColLeft" title="Insert Column Left">&#8592; Col</button>
        <button data-table-action="addColRight" title="Insert Column Right">&#8594; Col</button>
        <span class="table-toolbar-sep"></span>
        <button data-table-action="deleteRow" title="Delete Row">&#10005; Row</button>
        <button data-table-action="deleteCol" title="Delete Column">&#10005; Col</button>
        <span class="table-toolbar-sep"></span>
        <button data-table-action="mergeCells" title="Merge Cells">Merge</button>
        <button data-table-action="splitCell" title="Split Cell">Split</button>
        <span class="table-toolbar-sep"></span>
        <select data-table-action="borderStyle" title="Border Style">
            <option value="all">All Borders</option>
            <option value="outer">Outer Only</option>
            <option value="horizontal">Horizontal</option>
            <option value="none">No Borders</option>
        </select>
        <span class="table-toolbar-sep"></span>
        <input type="color" data-table-action="cellBg" title="Cell Background" value="#ffffff" />
        <button data-table-action="toggleBold" title="Bold Cell">B</button>
        <button data-table-action="deleteTable" title="Delete Table" class="table-btn-danger">&#128465;</button>
    `;
    return toolbar;
}

function showToolbar(table) {
    activeTable = table;
    if (!toolbar) createToolbar();

    const rect = table.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();

    toolbar.style.left = (rect.left - editorRect.left + editorEl.scrollLeft) + 'px';
    toolbar.style.top = (rect.top - editorRect.top + editorEl.scrollTop - 40) + 'px';

    // Update border select to match current
    const borderSel = toolbar.querySelector('[data-table-action="borderStyle"]');
    if (borderSel) borderSel.value = table.dataset.borderStyle || 'all';

    if (!toolbar.parentElement) editorEl.appendChild(toolbar);
}

function hideToolbar() {
    if (toolbar && toolbar.parentElement) toolbar.remove();
    activeTable = null;
}

// --- Context Menu ---

function showContextMenu(e, table) {
    e.preventDefault();
    hideContextMenu();
    activeTable = table;

    contextMenu = document.createElement('div');
    contextMenu.className = 'table-context-menu';
    contextMenu.contentEditable = 'false';
    contextMenu.innerHTML = `
        <button data-table-action="addRowAbove">Insert Row Above</button>
        <button data-table-action="addRowBelow">Insert Row Below</button>
        <button data-table-action="addColLeft">Insert Column Left</button>
        <button data-table-action="addColRight">Insert Column Right</button>
        <div class="table-ctx-sep"></div>
        <button data-table-action="deleteRow">Delete Row</button>
        <button data-table-action="deleteCol">Delete Column</button>
        <div class="table-ctx-sep"></div>
        <button data-table-action="mergeCells">Merge Cells</button>
        <button data-table-action="splitCell">Split Cell</button>
        <div class="table-ctx-sep"></div>
        <button data-table-action="toggleBold">Toggle Bold</button>
        <div class="table-ctx-sep"></div>
        <button data-table-action="deleteTable" class="table-btn-danger">Delete Table</button>
    `;
    contextMenu.style.left = e.clientX + 'px';
    contextMenu.style.top = e.clientY + 'px';
    document.body.appendChild(contextMenu);

    contextMenu.addEventListener('click', onTableAction);
    setTimeout(() => document.addEventListener('click', hideContextMenu, { once: true }), 0);
}

function hideContextMenu() {
    if (contextMenu) {
        contextMenu.remove();
        contextMenu = null;
    }
}

// --- Table Actions ---

function getActiveCell() {
    const sel = window.getSelection();
    if (!sel.rangeCount) return null;
    let node = sel.anchorNode;
    while (node && node !== editorEl) {
        if (node.nodeName === 'TD' || node.nodeName === 'TH') return node;
        node = node.parentNode;
    }
    return null;
}

function getCellIndex(cell) {
    const row = cell.parentElement;
    return { row: Array.from(row.parentElement.children).indexOf(row), col: Array.from(row.children).indexOf(cell) };
}

function getColCount(table) {
    const firstRow = table.querySelector('tr');
    if (!firstRow) return 0;
    let count = 0;
    for (const cell of firstRow.children) {
        count += (parseInt(cell.getAttribute('colspan')) || 1);
    }
    return count;
}

function onTableAction(e) {
    const btn = e.target.closest('[data-table-action]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const action = btn.dataset.tableAction;
    const cell = getActiveCell();

    if (action === 'addRowAbove' || action === 'addRowBelow') {
        if (!cell || !activeTable) return;
        const row = cell.parentElement;
        const cols = getColCount(activeTable);
        const newRow = document.createElement('tr');
        for (let i = 0; i < cols; i++) {
            const td = document.createElement('td');
            td.innerHTML = '&nbsp;';
            newRow.appendChild(td);
        }
        if (action === 'addRowAbove') {
            row.parentElement.insertBefore(newRow, row);
        } else {
            row.parentElement.insertBefore(newRow, row.nextSibling);
        }
    }
    else if (action === 'addColLeft' || action === 'addColRight') {
        if (!cell || !activeTable) return;
        const idx = getCellIndex(cell);
        const rows = activeTable.querySelectorAll('tr');
        rows.forEach(row => {
            const td = document.createElement('td');
            td.innerHTML = '&nbsp;';
            const ref = row.children[idx.col];
            if (action === 'addColLeft') {
                row.insertBefore(td, ref);
            } else {
                row.insertBefore(td, ref ? ref.nextSibling : null);
            }
        });
    }
    else if (action === 'deleteRow') {
        if (!cell) return;
        const row = cell.parentElement;
        if (row.parentElement.children.length > 1) {
            row.remove();
        }
    }
    else if (action === 'deleteCol') {
        if (!cell || !activeTable) return;
        const idx = getCellIndex(cell);
        const rows = activeTable.querySelectorAll('tr');
        rows.forEach(row => {
            if (row.children[idx.col]) row.children[idx.col].remove();
        });
    }
    else if (action === 'mergeCells') {
        mergeCells();
    }
    else if (action === 'splitCell') {
        splitCell(cell);
    }
    else if (action === 'borderStyle') {
        if (!activeTable) return;
        const val = btn.value || btn.dataset.value;
        activeTable.dataset.borderStyle = val;
    }
    else if (action === 'cellBg') {
        if (!cell) return;
        cell.style.backgroundColor = btn.value;
    }
    else if (action === 'toggleBold') {
        if (!cell) return;
        const isBold = cell.style.fontWeight === 'bold' || cell.style.fontWeight === '700';
        cell.style.fontWeight = isBold ? 'normal' : 'bold';
    }
    else if (action === 'deleteTable') {
        if (!activeTable) return;
        const p = document.createElement('p');
        p.innerHTML = '<br>';
        activeTable.replaceWith(p);
        hideToolbar();
    }

    hideContextMenu();
    markDirty();
}

// --- Merge / Split ---

function mergeCells() {
    const sel = window.getSelection();
    if (!sel.rangeCount || !activeTable) return;

    // Try to find selected cells (user selects across cells)
    const range = sel.getRangeAt(0);
    const cells = [];
    activeTable.querySelectorAll('td, th').forEach(cell => {
        if (range.intersectsNode(cell)) cells.push(cell);
    });

    if (cells.length < 2) return;

    // Simple merge: combine content into first cell, set colspan
    const first = cells[0];
    const firstRow = first.parentElement;
    let colspan = 0;
    let rowspan = 1;

    // Detect if cells span multiple rows
    const rows = new Set(cells.map(c => c.parentElement));
    if (rows.size > 1) rowspan = rows.size;

    cells.forEach((cell, i) => {
        colspan += (parseInt(cell.getAttribute('colspan')) || 1);
        if (i > 0) {
            if (cell.textContent.trim()) {
                first.innerHTML += ' ' + cell.innerHTML;
            }
            cell.remove();
        }
    });

    // For same-row merge
    if (rows.size === 1) {
        first.setAttribute('colspan', colspan);
    } else {
        // Multi-row: set both
        first.setAttribute('colspan', Math.ceil(colspan / rowspan));
        first.setAttribute('rowspan', rowspan);
    }
}

function splitCell(cell) {
    if (!cell) return;
    const colspan = parseInt(cell.getAttribute('colspan')) || 1;
    const rowspan = parseInt(cell.getAttribute('rowspan')) || 1;
    if (colspan <= 1 && rowspan <= 1) return;

    // Split colspan
    if (colspan > 1) {
        cell.removeAttribute('colspan');
        for (let i = 1; i < colspan; i++) {
            const td = document.createElement('td');
            td.innerHTML = '&nbsp;';
            cell.after(td);
        }
    }
    // Split rowspan
    if (rowspan > 1) {
        cell.removeAttribute('rowspan');
        const row = cell.parentElement;
        const idx = Array.from(row.children).indexOf(cell);
        let nextRow = row.nextElementSibling;
        for (let r = 1; r < rowspan; r++) {
            if (!nextRow) break;
            const td = document.createElement('td');
            td.innerHTML = '&nbsp;';
            if (nextRow.children[idx]) {
                nextRow.insertBefore(td, nextRow.children[idx]);
            } else {
                nextRow.appendChild(td);
            }
            nextRow = nextRow.nextElementSibling;
        }
    }
}

// --- Grid Picker (for inserting new tables) ---

export function showGridPicker(editorElement, callback) {
    const existing = document.querySelector('.table-grid-picker');
    if (existing) { existing.remove(); return; }

    const picker = document.createElement('div');
    picker.className = 'table-grid-picker';
    picker.contentEditable = 'false';

    const maxRows = 8, maxCols = 8;
    let hoverRow = 0, hoverCol = 0;

    const grid = document.createElement('div');
    grid.className = 'table-grid-cells';
    for (let r = 0; r < maxRows; r++) {
        for (let c = 0; c < maxCols; c++) {
            const cell = document.createElement('div');
            cell.className = 'table-grid-cell';
            cell.dataset.row = r;
            cell.dataset.col = c;
            grid.appendChild(cell);
        }
    }

    const label = document.createElement('div');
    label.className = 'table-grid-label';
    label.textContent = '0 × 0';

    picker.appendChild(grid);
    picker.appendChild(label);

    grid.addEventListener('mouseover', (e) => {
        const cell = e.target.closest('.table-grid-cell');
        if (!cell) return;
        hoverRow = parseInt(cell.dataset.row);
        hoverCol = parseInt(cell.dataset.col);
        label.textContent = `${hoverRow + 1} × ${hoverCol + 1}`;
        grid.querySelectorAll('.table-grid-cell').forEach(c => {
            const r = parseInt(c.dataset.row);
            const col = parseInt(c.dataset.col);
            c.classList.toggle('active', r <= hoverRow && col <= hoverCol);
        });
    });

    grid.addEventListener('click', (e) => {
        const cell = e.target.closest('.table-grid-cell');
        if (!cell) return;
        e.preventDefault();
        const rows = parseInt(cell.dataset.row) + 1;
        const cols = parseInt(cell.dataset.col) + 1;
        picker.remove();
        callback(rows, cols);
    });

    // Position near the table button
    const tableBtn = editorElement.closest('.blog-card')?.querySelector('[data-cmd="insertTable"]');
    if (tableBtn) {
        const rect = tableBtn.getBoundingClientRect();
        picker.style.position = 'fixed';
        picker.style.left = rect.left + 'px';
        picker.style.top = (rect.bottom + 4) + 'px';
    }
    document.body.appendChild(picker);

    // Close on outside click
    setTimeout(() => {
        document.addEventListener('click', function close(ev) {
            if (!picker.contains(ev.target)) {
                picker.remove();
                document.removeEventListener('click', close);
            }
        });
    }, 0);
}

function markDirty() {
    if (pageRef) {
        pageRef.blogDirty = true;
        if (pageRef.activeBlogPost) {
            pageRef.activeBlogPost.content_html = editorEl.innerHTML;
        }
        if (pageRef.renderBlogDirtyUI) pageRef.renderBlogDirtyUI();
    }
}

// --- Selection Change: detect active table ---

function onSelectionChange() {
    const sel = window.getSelection();
    if (!sel.rangeCount) { hideToolbar(); return; }

    let node = sel.anchorNode;
    while (node && node !== editorEl) {
        if (node.nodeName === 'TABLE') {
            showToolbar(node);
            return;
        }
        node = node.parentNode;
    }
    hideToolbar();
}

function onEditorContextMenu(e) {
    let node = e.target;
    while (node && node !== editorEl) {
        if (node.nodeName === 'TABLE') {
            showContextMenu(e, node);
            return;
        }
        if (node.nodeName === 'TD' || node.nodeName === 'TH') {
            showContextMenu(e, node.closest('table'));
            return;
        }
        node = node.parentNode;
    }
}

// Listen for border style and cell bg changes on toolbar
function onToolbarChange(e) {
    const el = e.target;
    if (el.dataset.tableAction === 'borderStyle' && activeTable) {
        activeTable.dataset.borderStyle = el.value;
        markDirty();
    }
    if (el.dataset.tableAction === 'cellBg') {
        const cell = getActiveCell();
        if (cell) {
            cell.style.backgroundColor = el.value;
            markDirty();
        }
    }
}

export function init(editor, page) {
    editorEl = editor;
    pageRef = page;

    document.addEventListener('selectionchange', onSelectionChange);
    editor.addEventListener('contextmenu', onEditorContextMenu);

    // Toolbar interactive controls
    const tb = createToolbar();
    tb.addEventListener('click', onTableAction);
    tb.addEventListener('change', onToolbarChange);
    tb.addEventListener('input', onToolbarChange);
}
